import {
	CurrencyInputField,
	CurrencyTypeProvider,
	FormDrawer,
	FormDrawerContent,
	FormDrawerContentProps,
	TextareaField,
} from "@dgs/core";
import { FormikErrors, useField } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { OrganizationalUserSelectField } from "../../../../shared/events/eventUsers/OrganizationalUserSelectField";
import { Currency } from "../../../../shared/types/currency";
import { ApprovingUserOptionResource } from "../../../../shared/types/organizationalUnitUser";
import { CurrencySingleSelect } from "../../../../shared/ui/CurrencySingleSelect";

export interface CreateEventUserRequestFormValues {
	participationReason: string;
	costs: number;
	currency: Currency | null;
	requestedApprover: ApprovingUserOptionResource | null;
}

interface Props {
	open: boolean;
	onClose: () => void;
	onSubmit: (values: CreateEventUserRequestFormValues) => Promise<boolean>;
}

const useValidate = () => {
	const { t } = useTranslation();

	return (values: CreateEventUserRequestFormValues) => {
		const errors: FormikErrors<CreateEventUserRequestFormValues> = {};

		if (!values.participationReason) {
			errors.participationReason = t("Required field");
		}
		if (values.currency === null) {
			errors.currency = t("Required field");
		}
		if (values.costs === null) {
			errors.costs = t("Required field");
		}
		if (values.requestedApprover === null) {
			errors.requestedApprover = t("Required field");
		}
		return errors;
	};
};

const initialValues: CreateEventUserRequestFormValues = {
	participationReason: "",
	costs: 0,
	currency: null,
	requestedApprover: null,
};

const Costs = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(2)};
`;

const EventUserRequestCreateDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();
	const [{ value: currency }] = useField<Currency | null>("currency");
	return (
		<FormDrawerContent {...props} submitButtonTitle={t("Request approval")}>
			<TextareaField name="participationReason" label={t("Participation reason")} required />
			<Costs>
				<CurrencySingleSelect name="currency" label={t("Currency")} required />
				<CurrencyTypeProvider currencyType={currency || undefined}>
					<CurrencyInputField name="costs" label={t("Costs")} required />
				</CurrencyTypeProvider>
			</Costs>
			<OrganizationalUserSelectField name="requestedApprover" label={t("Person responsible for cost")} required />
		</FormDrawerContent>
	);
};

export const EventUserRequestCreateDrawer: FC<Props> = ({ onSubmit, ...props }) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<FormDrawer
			{...props}
			validate={validate}
			renderForm={(props) => <EventUserRequestCreateDrawerContent {...props} />}
			initialValues={initialValues}
			onSubmit={onSubmit}
			title={t("Create request")}
		/>
	);
};
